const {getGndId} = require('../utils')

// http://ws.gbv.de/suggest/gnd/

const exactTypes = [
  'SubjectHeadingSensoStricto',
  'HistoricSingleEventOrEra',
  'GroupOfPersons',
  'CharactersOrMorphemes',
  'MeansOfTransportWithIndividual_name',
  'FictiveTerm',
  'NomenclatureInBiologyOrChemistry',
  'ProductNameOrBrandName',
  'SoftwareProduct',
  'Language',
  'EthnographicName',
  'MeansOfTransportWithIndividualName',
  'ReligiousTerritory',
  'NaturalGeographicUnit',
  'TerritorialCorporateBodyOrAdministrativeUnit',
  'FictivePlace',
  'BuildingOrMemorial',
  'Country',
  'AdministrativeUnit',
  'ExtraterrestrialTerritory',
  'MemberState',
  'WayBorderOrLine',
  'NameOfSmallGeographicUnitLyingWithinAnotherGeographicUnit',
  'FictiveCorporateBody',
  'OrganOfCorporateBody',
  'ProjectOrProgram',
  'Pseudonym',
  'LiteraryOrLegendaryCharacter',
  'Spirits',
  'RoyalOrMemberOfARoyalHouse',
  'CollectivePseudonym',
  'Gods',
]

function brJoin(arr) {
  return arr.map(t => t.preferredName).join('<br/>')
}

module.exports = class GbvGndPlugin {

  /**
   * ### AuthoritiesClient.plugin('gbv/gnd', opts)
   *
   * - `@param {object} opts` Options for constructor
   * - `@param {object} opts.endpointGet` URL of the entityFacts endpoint. Default: `http://hub.culturegraph.org/entityfacts`
   * - `@param {number} opts.endpointSuggest` URL of the suggestions service. Default: `https://ws.gbv.de/suggest/gnd/`
   */
  constructor(opts={}) {
    this.endpointGet = opts.endpointGet || 'http://hub.culturegraph.org/entityfacts'
    this.endpointSuggest = opts.endpointSuggest || 'https://ws.gbv.de/suggest/gnd/'
  }

  // -----------
  // PRIVATE API
  // -----------

  _entityFactsToInfobox(data) {

    let htmlContent = ''
    htmlContent += '<table style="border-spacing: 10px; border-collapse: separate;">'
    htmlContent += '<tr><td colspan="2"><h4>Informationen über den Eintrag</h4></td></tr>'

    // #########################
    // DifferentiatedPerson and CorporateBody

    // Vollständiger Name (DifferentiatedPerson + CorporateBody)
    htmlContent += `<tr><td>Name:</td><td>${data.preferredName}</td></tr>`

    // Abbildung (DifferentiatedPerson + CorporateBody)
    const {depiction} = data
    if (depiction && depiction.thumbnail) {
      htmlContent += `<tr><td>Abbildung:</td><td><img src="${depiction.thumbnail['@id']}" style="border: 0; max.width:120px; max-height:150px;" /></td></tr>`
    }

    // Lebensdaten (DifferentiatedPerson)
    const {dateOfBirth, dateOfDeath} = data
    if (dateOfBirth || dateOfDeath) {
      htmlContent += "<tr><td>Lebensdaten:</td><td>"
      if (dateOfBirth && dateOfDeath) {
        htmlContent += dateOfBirth + " bis " + dateOfDeath
      } else if (dateOfBirth && !dateOfDeath) {
        htmlContent += dateOfBirth + " bis unbekannt"
      } else if (!dateOfBirth && dateOfDeath) {
        htmlContent += `unbekannt bis ${dateOfDeath}`
      }
      htmlContent += "</td></tr>"
    }

    // Date of Establishment (CorporateBody)
    const {dateOfEstablishment} = data
    if (dateOfEstablishment) {
      htmlContent += `<tr><td>Gründung:</td><td>${dateOfEstablishment[0]}</td></tr>`
    }

    // Place of Business (CorporateBody)
    const {placeOfBusiness} = data
    if (placeOfBusiness && placeOfBusiness.length) {
      htmlContent += `<tr><td>Niederlassung(en):</td><td>${brJoin(placeOfBusiness)}</td></tr>`
    }

    // Übergeordnete Körperschaft (CorporateBody)
    const {hierarchicallySuperiorOrganisation} = data
    if (hierarchicallySuperiorOrganisation && hierarchicallySuperiorOrganisation.length) {
      htmlContent += `<tr><td>Übergeordnete Körperschaft(en):</td><td>${brJoin(hierarchicallySuperiorOrganisation)}</td></tr>`
    }

    // Geburtsort (DifferentiatedPerson)
    const {placeOfBirth} = data
    if (placeOfBirth)
      htmlContent += `<tr><td>Geburtsort:</td><td>${placeOfBirth[0].preferredName}</td></tr>`

    // Sterbeort (DifferentiatedPerson)
    const {placeOfDeath} = data
    if (placeOfDeath)
      htmlContent += `<tr><td>Sterbeort:</td><td>${placeOfDeath[0].preferredName}</td></tr>`

    // Berufe (DifferentiatedPerson)
    const {professionOrOccupation} = data
    if (professionOrOccupation && professionOrOccupation.length)
      htmlContent += `<tr><td>Beruf(e):</td><td>${brJoin(professionOrOccupation)}</td></tr>`

    // Biographie (DifferentiatedPerson)
    const {biographicalOrHistoricalInformation} = data
    if (biographicalOrHistoricalInformation)
      htmlContent += `<tr><td>Biographie:</td><td>${biographicalOrHistoricalInformation}</td></tr>`

    // Thema (CorporateBody)
    const {topic} = data
    if (topic && topic.length)
      htmlContent += `<tr><td>Themen:</td><td>${brJoin(topic)}</td></tr>`

    // Synonyme (DifferentiatedPerson + CorporateBody)
    const {variantName} = data
    if (variantName && variantName.length)
      htmlContent += `<tr><td>Synonyme:</td><td>${variantName.join("<br />")}</td></tr>`

    return htmlContent
  }


  /**
   * 
   * ### `infoBox(id)`
   * 
   * Promise a Wikipedia-Style Infobox about `id` as HTML.
   * 
   * - `@param {string} id`: GND of the entity to infoBox
   * - `@param {object} opts`:
   *   - `@param {string} opts.type`: Type restriction. Format infoBox for this one type. Must be 'DifferentiatedPerson' or 'CorporateBody'.
   * - `@return {Promise(string)}` A promise that resolves to the formatted HTML
   */
  infoBox(id, opts={}) {
    return new Promise((resolve, reject) => {
      const {type} = opts
      if (!type)
        throw new Error(`Must set 'type' option to either 'DifferentiatedPerson' or 'CorporateBody'`)
      if (!(type === 'DifferentiatedPerson' || type === 'CorporateBody'))
        throw new Error(`Can only infoBox 'DifferentiatedPerson' or 'CorporateBody', not '${type}'`)
      this.get(id)
        .then(entityFacts => resolve(this._entityFactsToInfobox(entityFacts)))
        .catch(err => reject(err))
    })
  }

  /**
   * 
   * ### `get(id)`
   *
   * Return all the data about entity with GND id
   *
   */
  get(id) {
    return new Promise((resolve, reject) => {
      fetch(`${this.endpointGet}/${encodeURIComponent(getGndId(id))}`)
        .then(x => x.json())
        .then(x => resolve(x))
        .catch(err => reject(err))
    })
  }

  /**
   * 
   * ### `search()`
   * 
   * Not implemented for this plugin
   *
   */
  search() {
    return Promise.reject("NIH")
  }

  /**
   * 
   * ### `suggest(term, opts)`
   * 
   * - `@param {string} term`: Search term
   * - `@param {object} opts`:
   *   - `@param {number} opts.count`: Number of results to return. Default: 10
   *   - `@param {string} opts.type`: Type restrictions. Results must be of one of these types.
   *     space-separated string. Default: no restriction
   *   - `@param {string} opts.format`: Must be `opensearch`
   *
   */
  suggest(term, opts={}) {
    if (opts.format !== 'opensearch') {
      return Promise.reject("Can only make suggestions with 'format' == 'opensearch'")
    }
    let query = {
      searchterm: term
    }

    query.count = opts.count || 10

    if (opts.type) {
      if (exactTypes.indexOf(opts.type) > -1) {
        query.exact_type = opts.type
      } else {
        query.type = opts.type
      }
    }


    const queryString = Object.keys(query).map((key) => {
      return encodeURIComponent(key) + '=' + encodeURIComponent(query[key]);
    }).join('&');
    
    return fetch(this.endpointSuggest + '?' + queryString).then(response => response.json())    
  }

}
